import { GenerativeConstraints, ScaleType, RootNote } from '../../types';
import {
    COMPOSER_PROGRESSIONS,
    COMPOSER_ROOTS,
    VIBE_NAMES,
    pickRandom,
    ComposerProgression
} from '../../utils/music/composerProgressions';
import { SongStructureDefinition, pickRandomStructure, SONG_STRUCTURES } from '../../utils/music/songStructures';

/**
 * AUDIT FIX: Song DNA - Establishes rhythmic and stylistic identity for the entire song
 * This is chosen ONCE at session start and persists throughout.
 */
export type RhythmicDNAType = 'Straight' | 'Gallop' | 'Tresillo' | 'Heartbeat' | 'Sweep' | 'Waltz';
export type GrooveVariant = 'tight' | 'loose' | 'swung';
export type IntensityBias = 'building' | 'steady' | 'ebbing';

export interface SongDNA {
    primaryRhythm: RhythmicDNAType;  // The main groove pattern for this song
    grooveVariant: GrooveVariant;    // How the groove feels (timing)
    intensityBias: IntensityBias;    // Overall energy arc tendency
}

/**
 * ComposerSession
 * Manages the STATE and CONFIGURATION of a Composer session.
 * Holds: Mood, Scale, Progression, Instrument, and dynamic state like Progression Index.
 */
export class ComposerSession {
    // Config / Immutable per session
    public rootNote: RootNote = 'C';
    public scale: ScaleType = 'major';
    public progression: ComposerProgression = COMPOSER_PROGRESSIONS[0];
    public vibeName: string = 'Unique Melody';
    public currentMood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery' = 'Calm';
    public octaveOffset: number = 0;
    public useInversions: boolean = false;
    public textureMode: 'chordal' | 'melodic' | 'mixed' = 'mixed';
    public suggestedInstrument: 'piano' | 'electric-piano' = 'piano';

    // Generative Structure (Phase 1)
    public structureDefinition: SongStructureDefinition = SONG_STRUCTURES[0];

    // The Hook Architect (Phase 3)
    // Stores the melody notes of the first Chorus to replace in future Choruses
    public hookMotif: (number | null)[] = [];
    public hasRecordedHook: boolean = false;

    // MUSICIAN BRAIN (Feature 8)
    public motifStrategy: 'classic' | 'invert' | 'retrograde' | 'expand' = 'classic';
    public phraseTightness: 'loose' | 'strict' = 'loose';

    // AUDIT FIX: Song-level rhythmic identity (persistent throughout session)
    public songDNA: SongDNA = {
        primaryRhythm: 'Straight',
        grooveVariant: 'tight',
        intensityBias: 'steady'
    };

    // Dynamic State
    public progressionIndex: number = 0;
    public currentKeyOffset: number = 0; // Semitones shift
    public touchCount: number = 0;
    public currentBassNote: number | null = null;
    public currentChordContext: number[] = [];
    public lastMelodyNote: number | null = null; // Counterpoint State
    public isInitialized: boolean = false;

    // EMOTIONAL MOOD INJECTION (Feature 6)
    public intensityHistory: number[] = [];
    public evolvedMood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery' | null = null;

    // AUDIT FIX 1.2: Allow SessionJourney to override harmony
    public journeyOverride?: number[];

    // HARMONIC MEMORY (Feature 7: The Virtuoso)
    // "The Rule of 5": Track 4-bar phrase + 1 resolution
    public chordHistory: Array<{
        degree: number;
        quality: string;
        notes: number[];
        rootMidi: number;
        isSecondaryDominant?: boolean;
    }> = [];

    public chordForecast: Array<{
        degree: number;
        quality: string;
        notes: number[];
        rootMidi: number;
        isSecondaryDominant?: boolean;
    }> = [];

    constructor() { }

    /**
     * initializes a new random session configuration.
     * @param forcePiano - If true, the suggested instrument will always be 'piano'
     */
    initialize(forcePiano: boolean = false): void {
        // 1. Pick a Mood
        const moods: Array<'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery'> = ['Happy', 'Sad', 'Energetic', 'Calm', 'Mystery'];
        this.currentMood = pickRandom(moods);

        // 2. Configure Session based on Mood
        this.configureSessionForMood(this.currentMood);

        // Core randomization
        this.rootNote = pickRandom(COMPOSER_ROOTS);

        // Random starting position
        this.progressionIndex = Math.floor(Math.random() * this.progression.degrees.length);

        // Random octave offset (-1, 0, or +1 octave)
        this.octaveOffset = Math.floor(Math.random() * 3) - 1;

        // Random: use inversions sometimes
        this.useInversions = Math.random() < 0.4;

        // Random Instrument Selection
        const instrumentOptions: Array<'piano' | 'electric-piano'> = [
            'piano', 'electric-piano'
        ];

        if (forcePiano) {
            this.suggestedInstrument = 'piano';
        } else {
            this.suggestedInstrument = pickRandom(instrumentOptions);
        }

        // Random Motif Strategy (The "Brain" Variance)
        const strategies: Array<'classic' | 'invert' | 'retrograde' | 'expand'> = ['classic', 'invert', 'retrograde', 'expand'];
        // Bias towards classic/invert, but allow others
        this.motifStrategy = Math.random() < 0.5 ? 'classic' : pickRandom(strategies);

        // Random Phrase Tightness
        this.phraseTightness = Math.random() < 0.5 ? 'loose' : 'strict';

        // Generate emotional vibe name
        const vibeOptions = VIBE_NAMES[this.progression.name] || ['Unique Melody'];
        this.vibeName = pickRandom(vibeOptions);

        // Random Structure Selection (Phase 1)
        this.structureDefinition = pickRandomStructure();

        // Reset Dynamic State
        this.touchCount = 0;
        this.currentBassNote = null;
        this.currentKeyOffset = 0;
        this.currentChordContext = [];
        this.intensityHistory = [];
        this.chordHistory = [];
        this.chordForecast = [];
        this.chordForecast = [];
        this.evolvedMood = null;
        this.isInitialized = true;

        // Reset Hook
        this.hookMotif = [];
        this.hasRecordedHook = false;

        // AUDIT FIX: Initialize Song DNA (persistent rhythmic identity)
        this.initializeSongDNA();

        if (process.env.NODE_ENV === 'development')
            console.log(`[ComposerSession] New session: "${this.vibeName}" (Mood: ${this.currentMood}, Struct: ${this.structureDefinition.name}, DNA: ${this.songDNA.primaryRhythm})`);
    }

    /**
     * EMOTIONAL MOOD INJECTION (Feature 6): Track user intensity and evolve mood.
     * @param velocity - Current touch velocity (0-1)
     */
    injectIntensity(velocity: number): void {
        this.intensityHistory.push(velocity);

        // Keep last 20 touches
        if (this.intensityHistory.length > 20) {
            this.intensityHistory.shift();
        }

        // Calculate average intensity
        const avg = this.intensityHistory.reduce((a, b) => a + b, 0) / this.intensityHistory.length;

        // Evolve mood based on intensity trends
        if (avg > 0.7) {
            // High energy playing
            if (this.currentMood === 'Calm') this.evolvedMood = 'Energetic';
            else if (this.currentMood === 'Sad') this.evolvedMood = 'Mystery';
        } else if (avg < 0.3) {
            // Soft, gentle playing
            if (this.currentMood === 'Energetic') this.evolvedMood = 'Happy';
            else if (this.currentMood === 'Happy') this.evolvedMood = 'Calm';
        } else {
            this.evolvedMood = null; // Stay in current mood
        }
    }

    /**
     * Get the effective mood, considering evolution.
     */
    getEffectiveMood(): 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery' {
        return this.evolvedMood || this.currentMood;
    }

    /**
     * Maps the selected Mood to appropriate Scales, Progressions, and Texture Modes.
     */
    private configureSessionForMood(mood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery') {
        const happyScales: ScaleType[] = ['major', 'pentatonic_major', 'lydian', 'mixolydian'];
        const sadScales: ScaleType[] = ['minor', 'pentatonic_minor', 'dorian'];

        // V11: Smart Progression Selection
        // Weighted random selection based on complexity to ensure "special" chords feel special.
        // 70% Simple/Medium (Foundational)
        // 30% Complex/Exotic (Special/Vibey)
        let allowedComplexities: ('Simple' | 'Medium' | 'Complex' | 'Exotic')[] = [];
        const roll = Math.random();

        if (roll < 0.7) {
            allowedComplexities = ['Simple', 'Medium'];
        } else {
            allowedComplexities = ['Complex', 'Exotic'];
        }

        // Filter valid progressions
        const validProgressions = COMPOSER_PROGRESSIONS.filter(p => {
            // 1. Check Mood (Loose matching, allows 'Any')
            // Cast to any to handle extended mood types in the progression list
            const moodMatch = p.moods.includes('Any') || p.moods.includes(mood as any);

            // 2. Check Complexity
            const complexityMatch = allowedComplexities.includes(p.complexity);

            return moodMatch && complexityMatch;
        });

        // Fallback
        let selectedProgression = validProgressions.length > 0
            ? pickRandom(validProgressions)
            : pickRandom(COMPOSER_PROGRESSIONS.filter(p => p.moods.includes('Any') || p.complexity === 'Simple'));

        this.progression = selectedProgression;

        switch (mood) {
            case 'Happy':
                this.scale = pickRandom(happyScales);
                this.textureMode = Math.random() < 0.6 ? 'mixed' : 'chordal';
                break;
            case 'Sad':
                this.scale = pickRandom(sadScales);
                this.textureMode = 'melodic';
                break;
            case 'Energetic':
                this.scale = 'major';
                this.textureMode = 'mixed';
                break;
            case 'Calm':
                this.scale = 'dorian';
                this.textureMode = 'chordal';
                break;
            case 'Mystery':
                this.scale = 'minor';
                this.textureMode = 'mixed';
                break;
        }

        if (!this.progression) this.progression = COMPOSER_PROGRESSIONS[0];
        if (!this.scale) this.scale = 'major';
    }

    /**
     * Generates constraints based on current session and PROVIDED section context.
     * Note: Does NOT own StructureEngine, so it asks for the section.
     */
    getConstraints(currentSection: 'Verse' | 'Chorus' | 'Bridge'): GenerativeConstraints {
        let rangeMin = 3 + this.octaveOffset;
        let rangeMax = 5 + this.octaveOffset;

        if (currentSection === 'Verse') {
            rangeMin = 3; rangeMax = 5;
        } else if (currentSection === 'Chorus') {
            rangeMin = 4; rangeMax = 6;
        } else if (currentSection === 'Bridge') {
            rangeMin = 2; rangeMax = 5;
        }

        const baseMin = rangeMin;
        const baseMax = rangeMax;

        return {
            enabled: true,
            scale: this.scale,
            rootNote: this.rootNote,
            octaveRange: { min: Math.max(2, baseMin), max: Math.min(6, baseMax) },
            quantization: 'off',
            tempo: 120
        };
    }

    /**
     * AUDIT FIX: Initialize Song DNA based on mood and random variance
     * This establishes the rhythmic identity for the entire song
     */
    private initializeSongDNA(): void {
        const rhythmTypes: RhythmicDNAType[] = ['Straight', 'Gallop', 'Tresillo', 'Heartbeat', 'Sweep', 'Waltz'];
        const grooveVariants: GrooveVariant[] = ['tight', 'loose', 'swung'];
        const intensityBiases: IntensityBias[] = ['building', 'steady', 'ebbing'];

        // Mood-influenced rhythm selection
        let rhythmWeights: Record<RhythmicDNAType, number> = {
            'Straight': 3,    // Default common
            'Gallop': 2,
            'Tresillo': 2,
            'Heartbeat': 1,
            'Sweep': 1,
            'Waltz': 1
        };

        // Adjust weights based on mood
        switch (this.currentMood) {
            case 'Energetic':
                rhythmWeights['Gallop'] = 4;
                rhythmWeights['Tresillo'] = 3;
                break;
            case 'Calm':
                rhythmWeights['Heartbeat'] = 3;
                rhythmWeights['Waltz'] = 3;
                rhythmWeights['Sweep'] = 0;
                break;
            case 'Sad':
                rhythmWeights['Heartbeat'] = 4;
                rhythmWeights['Straight'] = 2;
                break;
            case 'Happy':
                rhythmWeights['Straight'] = 4;
                rhythmWeights['Tresillo'] = 3;
                break;
            case 'Mystery':
                rhythmWeights['Tresillo'] = 4;
                rhythmWeights['Waltz'] = 3;
                break;
        }

        // Weighted random selection for rhythm
        const totalWeight = Object.values(rhythmWeights).reduce((a, b) => a + b, 0);
        let random = Math.random() * totalWeight;
        let selectedRhythm: RhythmicDNAType = 'Straight';

        for (const [rhythm, weight] of Object.entries(rhythmWeights) as [RhythmicDNAType, number][]) {
            if (random < weight) {
                selectedRhythm = rhythm;
                break;
            }
            random -= weight;
        }

        // Random groove variant and intensity bias
        const selectedGroove = pickRandom(grooveVariants);
        const selectedIntensity = pickRandom(intensityBiases);

        this.songDNA = {
            primaryRhythm: selectedRhythm,
            grooveVariant: selectedGroove,
            intensityBias: selectedIntensity
        };
    }
}
